import React, { useState, useEffect } from 'react';
import { Settings, Plus, Edit, Trash2, Search, ChevronUp, ChevronDown, X, AlertCircle } from 'lucide-react';
import { SystemSetting, CodeItem } from 'services/systemSettingsService';
import { getSystemSettingCode, getSystemSettings } from 'helpers/backend_helper';

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('systemsetting');
  const [systemSettings, setSystemSettings] = useState<SystemSetting[]>([]);
  const [codeList, setCodeList] = useState<CodeItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('');
  const [sortReverse, setSortReverse] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState<SystemSetting>({
    systemSettingId: 0,
    code: 170,
    name: '',
    image: '',
    imageUrl: '',
    value1: '',
    value2: '',
    value3: '',
  });

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        // For now, using mock data. Replace with actual API calls when backend is ready
        const codes = await getSystemSettingCode();
        const settings = await getSystemSettings();
        console.log(codes);
        console.log(settings);
        setCodeList(codes.data);
        setSystemSettings(settings.data);
        
        // Uncomment these lines when backend API is ready:
        // const codes = await SystemSettingsService.getSystemSettingCodes();
        // const settings = await SystemSettingsService.getSystemSettings();
        // setCodeList(codes);
        // setSystemSettings(settings);
      } catch (error) {
        console.error('Error loading system settings:', error);
        setError('Failed to load system settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortReverse(!sortReverse);
    } else {
      setSortBy(key);
      setSortReverse(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // For now, just set the filename. Replace with actual upload when backend is ready
        setFormData(prev => ({
          ...prev,
          image: file.name
        }));
        
        // Uncomment these lines when backend API is ready:
        // const uploadResult = await SystemSettingsService.uploadImage(file);
        // setFormData(prev => ({
        //   ...prev,
        //   image: uploadResult.data[1] // Assuming the API returns the image filename
        // }));
      } catch (error) {
        console.error('Error uploading file:', error);
        setError('Failed to upload image. Please try again.');
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.name || !formData.value1) {
      setError('Please fill all mandatory fields.');
      return;
    }

    try {
      // For now, using local state updates. Replace with actual API calls when backend is ready
      if (formData.systemSettingId === 0) {
        // Add new setting
        const newSetting = {
          ...formData,
          systemSettingId: Date.now(),
          codeName: codeList.find(c => c.listItemId === formData.code)?.name
        };
        setSystemSettings(prev => [...prev, newSetting]);
        setSuccess('System Setting has been added successfully.');
      } else {
        // Update existing setting
        setSystemSettings(prev => prev.map(setting => 
          setting.systemSettingId === formData.systemSettingId 
            ? { ...formData, codeName: codeList.find(c => c.listItemId === formData.code)?.name }
            : setting
        ));
        setSuccess('System Setting has been updated successfully.');
      }

      // Uncomment these lines when backend API is ready:
      // const result = await SystemSettingsService.addSystemSetting(formData);
      // if (result.systemSettingId === 0) {
      //   setSuccess('System Setting has been added successfully.');
      // } else {
      //   setSuccess('System Setting has been updated successfully.');
      // }
      // await loadData(); // Reload the data

      clearForm();
      setActiveTab('systemsetting');
    } catch (error) {
      console.error('Error saving system setting:', error);
      setError('Failed to save system setting. Please try again.');
    }
  };

  const handleEdit = (setting: SystemSetting) => {
    setFormData(setting);
    setActiveTab('addsystemsetting');
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      try {
        // For now, using local state updates. Replace with actual API calls when backend is ready
        setSystemSettings(prev => prev.filter(setting => setting.systemSettingId !== deleteId));
        setSuccess('System Setting has been deleted successfully.');
        
        // Uncomment these lines when backend API is ready:
        // await SystemSettingsService.deleteSystemSetting(deleteId);
        // setSuccess('System Setting has been deleted successfully.');
        // await loadData(); // Reload the data
        
        setShowDeleteModal(false);
        setDeleteId(null);
      } catch (error) {
        console.error('Error deleting system setting:', error);
        setError('Failed to delete system setting. Please try again.');
      }
    }
  };

  const clearForm = () => {
    setFormData({
      systemSettingId: 0,
      code: 170,
      name: '',
      image: '',
      imageUrl: '',
      value1: '',
      value2: '',
      value3: '',
    });
  };

  const resetTab = () => {
    clearForm();
    setActiveTab('systemsetting');
  };

  // Filter and sort data
  const filteredAndSortedData = systemSettings
    .filter(setting => 
      setting.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      setting.codeName?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (!sortBy) return 0;
      
      const aValue = a[sortBy as keyof SystemSetting];
      const bValue = b[sortBy as keyof SystemSetting];
      
      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortReverse ? -1 : 1;
      if (bValue === undefined) return sortReverse ? 1 : -1;
      
      if (aValue < bValue) return sortReverse ? 1 : -1;
      if (aValue > bValue) return sortReverse ? -1 : 1;
      return 0;
    });

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Settings className="w-8 h-8 mr-3" />
                System Settings
              </h1>
              <p className="text-gray-600 mt-2">Manage system configuration settings</p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
            <button
              onClick={() => setError('')}
              className="absolute top-0 right-0 mt-2 mr-2 text-red-400 hover:text-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            {success}
            <button
              onClick={() => setSuccess('')}
              className="absolute top-0 right-0 mt-2 mr-2 text-green-400 hover:text-green-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('systemsetting')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'systemsetting'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Settings className="inline-block w-4 h-4 mr-2" />
                  System Settings
                </button>
                <button
                  onClick={() => setActiveTab('addsystemsetting')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'addsystemsetting'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Plus className="inline-block w-4 h-4 mr-2" />
                  Add System Settings
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'systemsetting' && (
              <div>
                {loading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Loading system settings...</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h5 className="text-lg font-semibold flex items-center">
                        <Settings className="w-5 h-5 mr-2" />
                        System Settings
                      </h5>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {systemSettings.length === 0 ? (
                      <div className="text-center py-12">
                        <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No System Settings</h3>
                        <p className="text-gray-500 mb-4">Get started by adding your first system setting.</p>
                        <button
                          onClick={() => setActiveTab('addsystemsetting')}
                          className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add System Setting
                        </button>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th 
                                onClick={() => handleSort('codeName')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                Code
                                {sortBy === 'codeName' && (
                                  sortReverse ? <ChevronDown className="inline-block w-4 h-4 ml-1" /> : <ChevronUp className="inline-block w-4 h-4 ml-1" />
                                )}
                              </th>
                              <th 
                                onClick={() => handleSort('name')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                Name
                                {sortBy === 'name' && (
                                  sortReverse ? <ChevronDown className="inline-block w-4 h-4 ml-1" /> : <ChevronUp className="inline-block w-4 h-4 ml-1" />
                                )}
                              </th>
                              <th 
                                onClick={() => handleSort('image')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                Image
                                {sortBy === 'image' && (
                                  sortReverse ? <ChevronDown className="inline-block w-4 h-4 ml-1" /> : <ChevronUp className="inline-block w-4 h-4 ml-1" />
                                )}
                              </th>
                              <th 
                                onClick={() => handleSort('imageUrl')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                Image Url
                                {sortBy === 'imageUrl' && (
                                  sortReverse ? <ChevronDown className="inline-block w-4 h-4 ml-1" /> : <ChevronUp className="inline-block w-4 h-4 ml-1" />
                                )}
                              </th>
                              <th 
                                onClick={() => handleSort('value1')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                Value 1
                                {sortBy === 'value1' && (
                                  sortReverse ? <ChevronDown className="inline-block w-4 h-4 ml-1" /> : <ChevronUp className="inline-block w-4 h-4 ml-1" />
                                )}
                              </th>
                              <th 
                                onClick={() => handleSort('value2')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                Value 2
                                {sortBy === 'value2' && (
                                  sortReverse ? <ChevronDown className="inline-block w-4 h-4 ml-1" /> : <ChevronUp className="inline-block w-4 h-4 ml-1" />
                                )}
                              </th>
                              <th 
                                onClick={() => handleSort('value3')}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              >
                                Value 3
                                {sortBy === 'value3' && (
                                  sortReverse ? <ChevronDown className="inline-block w-4 h-4 ml-1" /> : <ChevronUp className="inline-block w-4 h-4 ml-1" />
                                )}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredAndSortedData.map((setting) => (
                              <tr key={setting.systemSettingId} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {setting.codeName}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {setting.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {setting.image && (
                                    <a 
                                      href={`/images/${setting.image}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-900"
                                    >
                                      Image
                                    </a>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {setting.imageUrl && (
                                    <div className="relative group">
                                      <span className="cursor-pointer text-blue-600 hover:text-blue-900">
                                        Image Url
                                      </span>
                                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white p-2 rounded text-xs max-w-xs z-10">
                                        <div dangerouslySetInnerHTML={{ __html: setting.imageUrl }} />
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {setting.value1}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {setting.value2}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {setting.value3}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => handleEdit(setting)}
                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  {setting.createdBy !== 'system' && (
                                    <button
                                      onClick={() => handleDelete(setting.systemSettingId)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {filteredAndSortedData.length === 0 && systemSettings.length > 0 && (
                      <div className="text-center py-8">
                        <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                        <p className="text-gray-500">Try adjusting your search terms.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'addsystemsetting' && (
              <div>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Code <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {codeList.map((code) => (
                          <option key={code.listItemId} value={code.listItemId}>
                            {code.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Image
                      </label>
                      <input
                        type="file"
                        name="image"
                        onChange={handleFileUpload}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">For Carousel Image Size (1920 × 929 pixels)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Image Url
                      </label>
                      <input
                        type="text"
                        name="imageUrl"
                        value={formData.imageUrl}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Value 1 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="value1"
                      value={formData.value1}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Value 2
                    </label>
                    <textarea
                      name="value2"
                      value={formData.value2}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Value 3
                    </label>
                    <textarea
                      name="value3"
                      value={formData.value3}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!formData.code || !formData.name || !formData.value1}
                      className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Confirmation Required?</h3>
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                <span className="text-blue-600 text-2xl font-bold">?</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                These items will be permanently deleted and cannot be recovered.
              </p>
              <p className="font-bold text-lg mb-6">Are you sure?</p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  NO
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  YES
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings; 